/**
 * TrendChart Component
 * Recharts ComposedChart with line + area gradient fill
 */

import React from 'react';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

const TrendChart = ({
  data = [],
  lines = [{ dataKey: 'value', color: '#3b82f6', label: 'Value' }],
  xKey = 'date',
  height = 300,
  title,
  ariaLabel,
  xFormatter,
}) => {
  const hasDualAxis = lines.some((l) => l.yAxisId === 'right');
  return (
    <div className="card p-5">
      {title && <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>}
      <div role="img" aria-label={ariaLabel || title || 'Trend chart'}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data}>
          <defs>
            {lines.map((line) => (
              <linearGradient key={`grad-${line.dataKey}`} id={`grad-${line.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={line.color} stopOpacity={0.15} />
                <stop offset="100%" stopColor={line.color} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={xFormatter} />
          {hasDualAxis ? (
            <>
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
                domain={lines.find((l) => (l.yAxisId || 'left') === 'left')?.domain}
                tickFormatter={lines.find((l) => (l.yAxisId || 'left') === 'left')?.tickFormatter}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
                domain={lines.find((l) => l.yAxisId === 'right')?.domain}
                tickFormatter={lines.find((l) => l.yAxisId === 'right')?.tickFormatter}
              />
            </>
          ) : (
            <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelFormatter={xFormatter}
            formatter={(value, name) => {
              const cfg = lines.find((l) => l.label === name);
              return cfg?.formatter ? [cfg.formatter(value), name] : [value, name];
            }}
          />
          <Legend />
          {lines.map((line) => (
            <React.Fragment key={line.dataKey}>
              <Area
                type="monotone"
                dataKey={line.dataKey}
                yAxisId={hasDualAxis ? (line.yAxisId || 'left') : undefined}
                fill={`url(#grad-${line.dataKey})`}
                stroke="none"
                legendType="none"
                tooltipType="none"
              />
              <Line
                type="monotone"
                dataKey={line.dataKey}
                yAxisId={hasDualAxis ? (line.yAxisId || 'left') : undefined}
                name={line.label}
                stroke={line.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
                connectNulls={false}
              />
            </React.Fragment>
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

export default React.memo(TrendChart);
